import { Platform, getPlatform } from '@/services/plugins/device';
import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponseHeaders,
  Method,
  RawAxiosRequestHeaders,
  RawAxiosResponseHeaders,
} from 'axios';

import config from '@/config';
import logger from '@/services/logger';

const REQUEST_FAILED = 'Request failed with status code ';
// 401 is deliberately absent: credentials the server rejected will be rejected
// by every other host too, so failing over only delays the real error.
const SERVICE_ERRORS = [
  'Network Error',
  `${REQUEST_FAILED}500`,
  `${REQUEST_FAILED}501`,
  `${REQUEST_FAILED}502`,
  `${REQUEST_FAILED}503`,
  `${REQUEST_FAILED}504`,
  `${REQUEST_FAILED}505`,
  `${REQUEST_FAILED}506`,
  `${REQUEST_FAILED}507`,
  `${REQUEST_FAILED}508`,
  `${REQUEST_FAILED}510`,
  `${REQUEST_FAILED}511`,
];

// An axios error serialises with its request config, which carries the
// Authorization header and the request body, so only these fields are logged.
export const describeRequestError = (error: any) => ({
  status: error?.response?.status,
  code: error?.code,
  message: error?.message ?? String(error),
});

export type Response = {
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
  data: unknown;
};

export type RequestHeaders =
  | (RawAxiosRequestHeaders &
      Partial<
        {
          [Key in Method as Lowercase<Key>]: AxiosHeaders;
        } & { common: AxiosHeaders }
      >)
  | AxiosHeaders;

export const sendDelete = async (
  url: string,
  additionalHeaders = {},
): Promise<Response> => {
  try {
    logger.debug('DELETE', { url });
    const { headers, data } = await axios.delete(
      url,
      await getConfig(additionalHeaders),
    );
    logger.debug('DELETE response', data);
    return { headers, data };
  } catch (error: any) {
    logger.error('DELETE', { url, error: describeRequestError(error) });
    throw error;
  }
};

export const get = async (
  baseUrls: string[],
  path: string,
  allowed404 = false,
  additionalHeaders = {},
): Promise<Response> => {
  const options = await getConfig(additionalHeaders);
  if (allowed404) {
    options.validateStatus = (status: number) =>
      [200, 201, 202, 204, 404].includes(status);
  }

  for (const baseUrl of baseUrls) {
    const url = baseUrl + path;
    try {
      logger.debug('GET', { url });
      const { headers, data } = await axios.get(url, options);
      logger.debug('GET response', data);
      return { headers, data };
    } catch (error: any) {
      if (SERVICE_ERRORS.includes(error.message)) {
        logger.warn('GET service error', {
          url,
          error: describeRequestError(error),
        });
        continue;
      } else {
        logger.error('GET application error', {
          url,
          error: describeRequestError(error),
        });
        throw error;
      }
    }
  }

  logger.error('GET No servers responded', { baseUrls, path });
  throw new Error(`GET ${path}, No servers responded: ${baseUrls}`);
};

export const post = async (
  baseUrls: string[],
  path: string,
  requestData: unknown,
  additionalHeaders = {},
): Promise<Response> => {
  for (const baseUrl of baseUrls) {
    const url = baseUrl + path;
    try {
      logger.debug('POST', { url, requestData });
      const { headers, data } = await axios.post(
        url,
        requestData,
        await getConfig(additionalHeaders),
      );
      logger.debug('POST response', data);
      return { headers, data };
    } catch (error: any) {
      if (SERVICE_ERRORS.includes(error.message)) {
        logger.warn('POST service error', {
          url,
          error: describeRequestError(error),
        });
        continue;
      } else {
        logger.error('POST application error', {
          url,
          error: describeRequestError(error),
        });
        throw error;
      }
    }
  }

  logger.error('POST No servers responded', { baseUrls, path });
  throw new Error(`POST ${path}, No servers responded: ${baseUrls}`);
};

export const put = async (
  baseUrls: string[],
  path: string,
  requestData: unknown,
  additionalHeaders = {},
): Promise<Response> => {
  for (const baseUrl of baseUrls) {
    const url = baseUrl + path;
    try {
      logger.debug('PUT', { url, requestData });
      const { headers, data } = await axios.put(
        url,
        requestData,
        await getConfig(additionalHeaders),
      );
      logger.debug('PUT response', data);
      return { headers, data };
    } catch (error: any) {
      if (SERVICE_ERRORS.includes(error.message)) {
        logger.warn('PUT service error', {
          url,
          error: describeRequestError(error),
        });
        continue;
      } else {
        logger.error('PUT application error', {
          url,
          error: describeRequestError(error),
        });
        throw error;
      }
    }
  }

  logger.error('PUT No servers responded', { baseUrls, path });
  throw new Error(`PUT ${path}, No servers responded: ${baseUrls}`);
};

export const uploadFile = async (
  baseUrls: string[],
  path: string,
  file: File,
  filename: string,
  additionalHeaders = {},
): Promise<Response> => {
  const formData = new FormData();
  formData.append('file', file, filename);

  for (const baseUrl of baseUrls) {
    const url = baseUrl + path;
    try {
      logger.debug('UPLOADFILE', { url, formData });
      const { headers, data } = await axios.post(
        url,
        formData,
        await getConfig(additionalHeaders),
      );
      logger.debug('UPLOADFILE response', data);
      return { headers, data };
    } catch (error: any) {
      if (SERVICE_ERRORS.includes(error.message)) {
        logger.warn('UPLOADFILE service error', {
          url,
          error: describeRequestError(error),
        });
        continue;
      } else {
        logger.error('UPLOADFILE application error', {
          url,
          error: describeRequestError(error),
        });
        throw error;
      }
    }
  }

  logger.error('UPLOADFILE No servers responded', { baseUrls, path });
  throw new Error(`UPLOADFILE ${path}, No servers responded: ${baseUrls}`);
};

export const getIntegraAppParts = async (): Promise<{
  appName: string;
  platformName: string;
}> => {
  const platform = await getPlatform();
  // The override names a brand-specific app registration on the Integra side,
  // which exists only for the deployed brands. Development traffic identifies
  // as the base app regardless of which brand is switched in.
  const appName =
    (config.env === 'development'
      ? undefined
      : config.app.integraAppOverride) ?? 'FreedomGoX';

  switch (platform) {
    case Platform.WEB:
      return { appName, platformName: 'WebBooking' };
    case Platform.ANDROID:
      return { appName, platformName: 'Android' };
    case Platform.IOS:
      return { appName, platformName: 'iOS' };
    default:
      logger.warn('getIntegraAppParts: Unknown platform', { platform });
      return { appName, platformName: 'iOS' };
  }
};

export const getIntegraApp = async (): Promise<string> => {
  const { appName, platformName } = await getIntegraAppParts();
  return `${appName},${platformName}`;
};

const getConfig = async (
  additionalHeaders: RequestHeaders,
): Promise<AxiosRequestConfig> => {
  const headers = {
    ...additionalHeaders,
    'Content-Type': 'application/json',
    'integra-app': await getIntegraApp(),
  };

  return { timeout: config.services.apiMsTimeout, headers };
};
